import { WidgetConfig, ThemeConfig } from "../store/types";
import { defaultTheme } from "../hooks/useVoiceWidget";

/**
 * Fallback glass UI for known handoff widgets when admin has not set mode.
 * When server sends mode/uiVariant, those win (see mergeServerConfig).
 * Temporary until Winterbach/Bachofer rows own mode in widget_config.
 */
const SPLIT_SWITCH_WIDGET_IDS = new Set([
  "2f61474e-30a3-49fb-b5bd-607cd1d27802", // Winterbach handoff widget
  "bachofer-eviivo", // Hotel Bachofer eviivo test widget
  "39c6dc37-bbf1-4e42-a647-d86db9d45710", // Hotel Bachofer live embed
]);

// Deployments where agent-suggested link buttons are hidden (cards stay as
// info). Winterbach: the agent sometimes emits valid-but-wrong winterbach.de
// URLs and the widget can't verify them cross-origin, so the buttons are hidden
// for the acceptance. Booking buttons (eviivo) are a separate path, unaffected.
const LINK_BUTTONS_DISABLED_WIDGET_IDS = new Set([
  "2f61474e-30a3-49fb-b5bd-607cd1d27802", // Winterbach handoff widget
]);

const DEFAULT_IDLE_TIMEOUT_MS = 60_000;
const SPLIT_SWITCH_IDLE_TIMEOUT_MS = 180_000;

export interface ProcessedConfig extends WidgetConfig {
  apiUrl: string;
  signedUrlEndpoint: string;
}

/**
 * Public wire shape from GET /api/widget/config and signed-url.widgetConfig.
 * Keep field set in sync with backend `PublicWidgetConfig`
 * (`backend/src/services/widget-public-config.ts`). Server position is L/R only;
 * embed may still use top/custom via processConfig + shell helper.
 */
export interface ServerWidgetConfig {
  widgetName?: string;
  welcomeMessage?: string | null;
  gpsEnabled?: boolean;
  mode?: "voice" | "chat" | "combined";
  uiVariant?: "legacy" | "split-switch";
  avatarUrl?: string;
  /** Public API emits only bottom-right | bottom-left. */
  position?: "bottom-right" | "bottom-left";
}

class ConfigurationService {
  /**
   * Admin/server fields override stale page embeds when present.
   * Unset server keys are ignored so embed GPS (e.g. Infomax) is not killed.
   */
  static mergeServerConfig(
    embed: WidgetConfig,
    server?: ServerWidgetConfig | null
  ): WidgetConfig {
    if (!server) return embed;

    const merged: WidgetConfig = { ...embed };
    // Production: server-present keys win so admin Text/Voice/GPS productization
    // applies without re-pasting WordPress. Unset server keys leave embed.
    // Admin Live preview skips the public-config fetch entirely (see
    // loadPublicConfig: false) — it does not use a merge fork.
    if (server.mode !== undefined) merged.mode = server.mode;
    if (server.uiVariant !== undefined) merged.uiVariant = server.uiVariant;
    if (server.gpsEnabled !== undefined) merged.gpsEnabled = server.gpsEnabled;
    if (server.widgetName !== undefined) merged.widgetName = server.widgetName;
    if (server.welcomeMessage !== undefined) {
      merged.welcomeMessage = server.welcomeMessage;
    }
    if (server.avatarUrl !== undefined) merged.avatarUrl = server.avatarUrl;
    if (server.position !== undefined) merged.position = server.position;
    return merged;
  }

  static processConfig(config: WidgetConfig): ProcessedConfig {
    // Auto-detect API URL
    const apiUrl = this.getApiUrl();
    const signedUrlEndpoint = `${apiUrl}/api/signed-url`;

    // Apply theme defaults
    const processedTheme = this.processTheme(config.theme);

    // Fallback: if only widgetId is provided, use it as agentId
    // This allows the widget to work with widgetId-only configuration
    // (Backend can lookup agent by either agentId or widgetId)
    const effectiveAgentId = config.agentId || config.widgetId || "";
    const effectiveWidgetId = config.widgetId || effectiveAgentId;
    const uiVariant = config.uiVariant ??
      (SPLIT_SWITCH_WIDGET_IDS.has(effectiveWidgetId) ? "split-switch" : "legacy");
    const linkButtonsEnabled = !LINK_BUTTONS_DISABLED_WIDGET_IDS.has(effectiveWidgetId);

    return {
      position: "bottom-right",
      offset: { x: 20, y: 20 },
      autoStart: false,
      debug: false,
      mode: "combined",
      uiVariant,
      linkButtonsEnabled,
      language: "de",
      idleTimeoutMs: uiVariant === "split-switch"
        ? SPLIT_SWITCH_IDLE_TIMEOUT_MS
        : DEFAULT_IDLE_TIMEOUT_MS,
      ...config,
      // agentId: effectiveAgentId,
      widgetId: effectiveWidgetId,
      theme: processedTheme,
      apiUrl,
      signedUrlEndpoint,
    };
  }

  static processTheme(theme?: Partial<ThemeConfig>): Partial<ThemeConfig> {
    if (!theme) return {};

    const processed = { ...theme };

    // Apply smart defaults based on primary color
    if (theme.primaryColor && !theme.buttonBackground) {
      processed.buttonBackground = theme.primaryColor;
      processed.userMessageBackground = theme.primaryColor;
    }

    return processed;
  }

  static getApiUrl(): string {
    // Check for explicit global configuration first
    if (typeof window !== "undefined" && (window as any).REMSTAL_API_URL) {
      return (window as any).REMSTAL_API_URL;
    }

    // Use build-time configured API URL
    const buildTimeApiUrl = process.env.WIDGET_API_URL;
    if (buildTimeApiUrl) {
      return buildTimeApiUrl;
    }

    // Fallback to localhost for development
    console.warn(
      "[ConfigurationService] No API URL configured, falling back to localhost:3000"
    );
    return "http://localhost:3000";
  }

  static validateConfig(config: WidgetConfig): string[] {
    const errors: string[] = [];

    // Accept either agentId or widgetId (widgetId can be used as agentId fallback)
    if (!config.agentId && !config.widgetId) {
      errors.push("agentId or widgetId is required");
    }

    if (
      config.position &&
      ![
        "bottom-right",
        "bottom-left",
        "top-right",
        "top-left",
        "custom",
      ].includes(config.position)
    ) {
      errors.push("Invalid position value");
    }

    if (config.mode && !["voice", "chat", "combined"].includes(config.mode)) {
      errors.push("Invalid mode value");
    }

    if (config.uiVariant && !["legacy", "split-switch"].includes(config.uiVariant)) {
      errors.push("Invalid uiVariant value");
    }

    return errors;
  }
}

export default ConfigurationService;
